/**
 * Client Orders List Page
 * Client-facing order history
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import Pagination from '../../../components/ui/Pagination';
import Badge from '../../../components/ui/Badge';
import type { OrderWithItems } from '@phoenixbill/shared/types/order';

export default function ClientOrdersListPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadOrders();
  }, [currentPage]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/client/orders', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
        },
      });
      const data = response.data.data;

      setOrders(data.orders);
      setTotalPages(data.pagination.totalPages);
      setTotalItems(data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      active: { variant: 'success', label: 'Active' },
      fraud: { variant: 'danger', label: 'Under Review' },
      cancelled: { variant: 'default', label: 'Cancelled' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      unpaid: { variant: 'warning', label: 'Unpaid' },
      paid: { variant: 'success', label: 'Paid' },
      refunded: { variant: 'default', label: 'Refunded' },
    };
    const config = statusMap[status] || statusMap.unpaid;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-100 mb-2">My Orders</h1>
        <p className="text-slate-400">View your order history and track your purchases</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-12 text-center">
          <svg
            className="mx-auto h-16 w-16 text-slate-600 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-xl font-semibold text-gray-200 mb-2">No orders yet</h3>
          <p className="text-slate-400 mb-6">Start shopping to see your orders here</p>
          <button
            onClick={() => navigate('/client/products')}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Browse Products
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                onClick={() => navigate(`/client/orders/${order.id}`)}
                className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 hover:border-slate-600 transition-colors cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-100 mb-1">
                      Order #{order.orderNumber}
                    </h3>
                    <p className="text-sm text-slate-400">
                      {new Date(order.createdAt).toLocaleDateString()} at{' '}
                      {new Date(order.createdAt).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(order.status)}
                    {getPaymentBadge(order.paymentStatus)}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Items</h4>
                  <div className="space-y-2">
                    {order.items.slice(0, 3).map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between items-start text-sm bg-slate-900 rounded p-3"
                      >
                        <div className="flex-1">
                          <p className="text-gray-200">{item.productName}</p>
                          <p className="text-gray-500 text-xs">
                            {item.billingCycle} × {item.quantity}
                          </p>
                        </div>
                        <p className="text-gray-200 font-medium">{formatPrice(item.total)}</p>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-xs text-gray-500">
                        +{order.items.length - 3} more item(s)
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-slate-700/50">
                  <div className="text-sm text-slate-400">
                    {order.items.length} item(s)
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Total</p>
                    <p className="text-xl font-bold text-gray-100">{formatPrice(order.total)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
